/**
 * Sticky Header functionality
 * Extracted from header.liquid lines 423-550
 * Optimized for performance with better event handling
 * Only includes menu animation (no header slide animation)
 */

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // Bind methods once for better performance
    this.onScrollHandler = this.onScroll.bind(this);
    this.setHeaderHeight = this.setHeaderHeight.bind(this);
    this.onMediaChange = this.setHeaderHeight.bind(this);
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
    this.headerBounds = {};

    this.setHeaderHeight();

    // Use MediaQueryList for responsive changes
    this.mediaQuery = window.matchMedia('(max-width: 990px)');
    this.mediaQuery.addEventListener('change', this.onMediaChange);

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.predictiveSearch = this.querySelector('predictive-search');
    this.inlineMenu = this.querySelector('.header__inline-menu');

    // Use passive event listener for better scroll performance
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    this.mediaQuery.removeEventListener('change', this.onMediaChange);

    // Clean up observer if it exists
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    this.observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    // Handle inline menu visibility for desktop only (ANIMATION TO KEEP)
    if (window.innerWidth >= 990 && this.inlineMenu) {
      const hideThreshold = this.headerBounds.bottom * 0.2; // Disappears at 20% of header height

      if (scrollTop > hideThreshold) {
        this.inlineMenu.classList.add('header__inline-menu--hidden');
      } else {
        this.inlineMenu.classList.remove('header__inline-menu--hidden');
      }
    }

    // Basic header scroll state (no animation - just class for styling)
    if (scrollTop > this.headerBounds.bottom) {
      // Past header - add scrolled class for potential styling
      this.header.classList.add('scrolled-past-header');
    } else if (scrollTop <= this.headerBounds.top) {
      // At top of page - remove scrolled class
      this.header.classList.remove('scrolled-past-header');
    }

    this.currentScrollTop = scrollTop;
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) {
      this.searchModal.close(false);
    }
  }
}

// Register the custom element
if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}